/**
 * The recent-conversations list on the Conversations page (SA-961, spec
 * §2.4): New conversation, then one row per conversation with rename and
 * delete. It moved out of the sidebar unchanged, over the same
 * useConversations calls, except that deleting the current conversation
 * lands on /conversations (replacing the deleted one's address), whose
 * redirect follows whatever the store makes current. `onNavigate` runs after
 * a pick or a create, so the drawer below `xl` can close.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Button, Spinner } from '@/components/ui'
import { useConversations } from '@/hooks'
import { useAppStore } from '@/stores'
import { ConversationListItem } from './ConversationListItem'
import { NewConversationModal } from './NewConversationModal'

interface ConversationListProps {
  /** Called after a conversation is opened or created. */
  onNavigate?: () => void
}

export function ConversationList({ onNavigate }: ConversationListProps) {
  const navigate = useNavigate()
  const [showNewConversationModal, setShowNewConversationModal] = useState(false)
  const activePersonaId = useAppStore((s) => s.activePersonaId)
  const {
    conversations,
    currentConversationId,
    isLoading,
    isSyncing,
    createConversation,
    selectConversation,
    deleteConversation,
    updateConversation,
  } = useConversations()

  const handleCreateConversation = async (personaId: string | null) => {
    const id = await createConversation(personaId)
    navigate(`/conversations/${id}`)
    onNavigate?.()
  }

  const handleSelectConversation = async (id: string) => {
    await selectConversation(id)
    navigate(`/conversations/${id}`)
    onNavigate?.()
  }

  const handleDeleteConversation = async (id: string) => {
    try {
      const wasCurrentConversation = id === currentConversationId
      // Delete first so the conversation is removed from the store before
      // any navigation triggers a re-fetch or auto-redirect.
      await deleteConversation(id)
      // Replace, not push: Back must not return to the deleted conversation.
      if (wasCurrentConversation) {
        navigate('/conversations', { replace: true })
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
    }
  }

  const handleRenameConversation = async (id: string, newTitle: string) => {
    try {
      await updateConversation(id, { title: newTitle })
    } catch (error) {
      console.error('Failed to rename conversation:', error)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border p-2">
        <Button
          size="sm"
          className="w-full"
          leftIcon={<Plus className="h-4 w-4" />}
          isLoading={isSyncing}
          onClick={() => setShowNewConversationModal(true)}
        >
          New conversation
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner />
          </div>
        ) : conversations.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">No conversations yet</p>
        ) : (
          conversations.map((conversation) => (
            <ConversationListItem
              key={conversation.id}
              conversation={conversation}
              isActive={conversation.id === currentConversationId}
              onClick={() => handleSelectConversation(conversation.id)}
              onDelete={() => handleDeleteConversation(conversation.id)}
              onRename={(newTitle) => handleRenameConversation(conversation.id, newTitle)}
            />
          ))
        )}
      </div>

      <NewConversationModal
        open={showNewConversationModal}
        onOpenChange={setShowNewConversationModal}
        onCreateConversation={handleCreateConversation}
        defaultPersonaId={activePersonaId}
      />
    </div>
  )
}
